import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  ArchiveRestore,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const EMPTY_ITEM = {
  title: "",
  description: "",
  priority: "P2",
  status: "planned",
  category: "",
  planning_horizon: "",
};

const STATUS_LABELS = {
  planned: "Planned",
  in_progress: "In progress",
  completed: "Completed",
  archived: "Archived",
};

const statusStyle = (status) => {
  if (status === "completed")
    return { background: "#E7F1EA", color: "#155E3F" };
  if (status === "in_progress")
    return { background: "#FFF4E2", color: "#8A5200" };
  if (status === "archived") return { background: "#F1F1EF", color: "#656560" };
  return { background: "#E8EEF3", color: "#25496C" };
};

const priorityStyle = (priority) => {
  if (priority === "P0") return { background: "#FBE9E7", color: "#9B2C1C" };
  if (priority === "P1") return { background: "#FFF4E2", color: "#8A5200" };
  if (priority === "P2") return { background: "#E8EEF3", color: "#25496C" };
  return { background: "#F1F1EF", color: "#656560" };
};

const dateLabel = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

export default function AdminBacklog() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("active");
  const [priority, setPriority] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ITEM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backlog", {
        params: {
          view,
          priority: priority || undefined,
          q: query || undefined,
        },
      });
      setItems(data.items || []);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [priority, query, view]);

  useEffect(() => {
    const timer = window.setTimeout(load, query ? 220 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);

  const summary = useMemo(
    () => ({
      planned: items.filter((item) => item.status === "planned").length,
      underway: items.filter((item) => item.status === "in_progress").length,
      published: items.filter((item) => item.published).length,
    }),
    [items],
  );

  const openCreate = () => {
    setEditing({ mode: "create" });
    setForm(EMPTY_ITEM);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title,
      description: item.description,
      priority: item.priority,
      status: item.status === "archived" ? "planned" : item.status,
      category: item.category || "",
      planning_horizon: item.planning_horizon || "",
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving("form");
    try {
      if (editing?.mode === "create") {
        await api.post("/admin/backlog", form);
        toast.success("Feature request added to the backlog");
      } else {
        await api.put(`/admin/backlog/${editing.id}`, form);
        toast.success("Backlog item updated");
      }
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const updatePublication = async (item) => {
    setSaving(`publish-${item.id}`);
    try {
      await api.put(`/admin/backlog/${item.id}/publication`, {
        published: !item.published,
      });
      toast.success(
        item.published
          ? "Removed from employer roadmap"
          : "Published to employer roadmap",
      );
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const toggleArchive = async (item) => {
    const status = item.status === "archived" ? "planned" : "archived";
    setSaving(`archive-${item.id}`);
    try {
      await api.put(`/admin/backlog/${item.id}`, { status });
      toast.success(
        status === "archived"
          ? "Backlog item archived"
          : "Backlog item restored",
      );
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  return (
    <div
      className="mx-auto max-w-[1400px] px-6 py-10 md:py-12 lg:px-10"
      data-testid="admin-backlog-page"
    >
      <div
        className="flex flex-col gap-5 border-b pb-8 md:flex-row md:items-end md:justify-between"
        style={{ borderColor: "var(--border)" }}
      >
        <div>
          <div className="eyebrow mb-2">System planning</div>
          <h1
            className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tight"
            style={{ color: "var(--ink)" }}
          >
            Product backlog
          </h1>
          <p
            className="mt-3 max-w-2xl text-sm"
            style={{ color: "var(--muted)" }}
          >
            Keep internal work concise, then deliberately choose the
            improvements employers can see.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary flex items-center gap-2 self-start"
          onClick={openCreate}
          data-testid="add-backlog-item"
        >
          <Plus size={14} /> Add Feature Request
        </button>
      </div>

      <div
        className="mt-6 grid grid-cols-3 border"
        style={{ borderColor: "var(--border)" }}
        data-testid="backlog-summary"
      >
        <Metric label="Planned" value={summary.planned} />
        <Metric label="In progress" value={summary.underway} />
        <Metric label="Published" value={summary.published} />
      </div>

      <div
        className="mt-6 flex flex-col gap-4 border p-4 md:flex-row md:items-end"
        style={{ borderColor: "var(--border)", background: "var(--surface)" }}
      >
        <div className="min-w-0 flex-1">
          <label className="field-label">Search</label>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-0 top-3.5"
              style={{ color: "var(--muted)" }}
            />
            <input
              className="input-flat !pl-6"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Title, description, or category"
              data-testid="backlog-search"
            />
          </div>
        </div>
        <Filter
          label="View"
          value={view}
          onChange={setView}
          testId="backlog-view-filter"
          options={[
            ["active", "Active planning"],
            ["planned", "Planned"],
            ["in_progress", "In progress"],
            ["completed", "Completed"],
            ["archived", "Archived"],
            ["all", "All items"],
          ]}
        />
        <Filter
          label="Priority"
          value={priority || "all"}
          onChange={(value) => setPriority(value === "all" ? "" : value)}
          testId="backlog-priority-filter"
          options={[
            ["all", "All priorities"],
            ["P0", "P0"],
            ["P1", "P1"],
            ["P2", "P2"],
            ["P3", "P3"],
          ]}
        />
      </div>

      <div
        className="mt-6 border max-h-[560px] overflow-auto"
        style={{ borderColor: "var(--border)", background: "var(--surface)" }}
        data-testid="backlog-list-region"
      >
        <table
          className="w-full min-w-[980px] text-xs"
          data-testid="backlog-table"
        >
          <thead>
            <tr
              className="border-b text-left eyebrow"
              style={{ borderColor: "var(--border)" }}
            >
              <th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">Priority</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Roadmap</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-3 py-8 text-center" colSpan={6}>
                  Loading backlog…
                </td>
              </tr>
            )}
            {!loading && !items.length && (
              <tr>
                <td
                  className="px-3 py-10 text-center text-sm"
                  style={{ color: "var(--muted)" }}
                  colSpan={6}
                >
                  No backlog items match these filters.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((item) => (
                <tr
                  key={item.id}
                  className="border-b align-top"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`backlog-row-${item.id}`}
                >
                  <td className="max-w-[470px] px-3 py-3">
                    <div
                      className="font-semibold text-sm"
                      style={{ color: "var(--ink)" }}
                    >
                      {item.title}
                    </div>
                    <div
                      className="mt-1 leading-relaxed"
                      style={{ color: "var(--muted)" }}
                    >
                      {item.description}
                    </div>
                    {item.category && (
                      <span className="mt-2 inline-block pill text-[9px]">
                        {item.category}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span className="pill" style={priorityStyle(item.priority)}>
                      {item.priority}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="pill" style={statusStyle(item.status)}>
                      {STATUS_LABELS[item.status]}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className="pill"
                      style={
                        item.published
                          ? { background: "#E7F1EA", color: "#155E3F" }
                          : { background: "#F1F1EF", color: "#656560" }
                      }
                    >
                      {item.published ? "Published" : "Internal"}
                    </span>
                  </td>
                  <td className="px-3 py-3" style={{ color: "var(--muted)" }}>
                    {dateLabel(item.created_at)}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      <IconButton
                        icon={Pencil}
                        label={`Edit ${item.title}`}
                        testId={`edit-backlog-${item.id}`}
                        onClick={() => openEdit(item)}
                      />
                      <IconButton
                        icon={item.published ? EyeOff : Eye}
                        label={
                          item.published
                            ? `Unpublish ${item.title}`
                            : `Publish ${item.title}`
                        }
                        testId={`publish-backlog-${item.id}`}
                        disabled={saving === `publish-${item.id}`}
                        onClick={() => updatePublication(item)}
                      />
                      <IconButton
                        icon={
                          item.status === "archived" ? ArchiveRestore : Archive
                        }
                        label={
                          item.status === "archived"
                            ? `Restore ${item.title}`
                            : `Archive ${item.title}`
                        }
                        testId={`archive-backlog-${item.id}`}
                        disabled={saving === `archive-${item.id}`}
                        onClick={() => toggleArchive(item)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(null)}
      >
        <DialogContent
          className="!rounded-none max-w-2xl"
          data-testid="backlog-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              {editing?.mode === "create"
                ? "Add Feature Request"
                : "Edit Backlog Item"}
            </DialogTitle>
            <DialogDescription>
              Items stay internal until you publish them to the employer
              roadmap.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-5" onSubmit={save}>
            <Field label="Title" required>
              <input
                className="input-flat"
                required
                value={form.title}
                onChange={(event) =>
                  setForm({ ...form, title: event.target.value })
                }
                data-testid="backlog-title-input"
              />
            </Field>
            <Field label="Plain-language description" required>
              <textarea
                className="input-flat min-h-28 resize-y"
                required
                value={form.description}
                onChange={(event) =>
                  setForm({ ...form, description: event.target.value })
                }
                data-testid="backlog-description-input"
              />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Priority">
                <SelectInput
                  value={form.priority}
                  onChange={(value) => setForm({ ...form, priority: value })}
                  testId="backlog-priority-input"
                  options={[
                    ["P0", "P0"],
                    ["P1", "P1"],
                    ["P2", "P2"],
                    ["P3", "P3"],
                  ]}
                />
              </Field>
              <Field label="Status">
                <SelectInput
                  value={form.status}
                  onChange={(value) => setForm({ ...form, status: value })}
                  testId="backlog-status-input"
                  options={[
                    ["planned", "Planned"],
                    ["in_progress", "In progress"],
                    ["completed", "Completed"],
                  ]}
                />
              </Field>
              <Field label="Category">
                <input
                  className="input-flat"
                  value={form.category}
                  onChange={(event) =>
                    setForm({ ...form, category: event.target.value })
                  }
                  data-testid="backlog-category-input"
                />
              </Field>
            </div>
            <Field label="Employer-facing planning horizon (optional)">
              <input
                className="input-flat"
                placeholder="e.g. Next"
                value={form.planning_horizon}
                onChange={(event) =>
                  setForm({ ...form, planning_horizon: event.target.value })
                }
                data-testid="backlog-horizon-input"
              />
            </Field>
            <DialogFooter>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setEditing(null)}
                data-testid="backlog-editor-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving === "form"}
                data-testid="backlog-editor-save"
              >
                {saving === "form" ? "Saving…" : "Save Item"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div
      className="border-r px-4 py-3 last:border-r-0"
      style={{ borderColor: "var(--border)" }}
    >
      <div className="eyebrow text-[9px]">{label}</div>
      <div
        className="mt-1 font-heading text-lg font-bold"
        style={{ color: "var(--ink)" }}
      >
        {value}
      </div>
    </div>
  );
}

function Filter({ label, value, onChange, options, testId }) {
  return (
    <label className="block min-w-[150px] text-xs">
      <span className="field-label">{label}</span>
      <select
        className="input-flat"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      >
        {options.map(([key, text]) => (
          <option key={key} value={key}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

function SelectInput({ value, onChange, options, testId }) {
  return (
    <select
      className="input-flat"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      data-testid={testId}
    >
      {options.map(([key, text]) => (
        <option key={key} value={key}>
          {text}
        </option>
      ))}
    </select>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="field-label">
        {label}
        {required && " *"}
      </span>
      {children}
    </label>
  );
}

function IconButton({ icon: Icon, label, testId, ...props }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      className="btn-ghost !p-2"
      data-testid={testId}
      {...props}
    >
      <Icon size={14} />
    </button>
  );
}
